import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Globe, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { formatApiError, mediaUrl } from "../lib/api";
import { EText } from "../components/cms/Editable";
import { useAuth } from "../context/AuthContext";

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)); }, []);

  const register = async (ev) => {
    try {
      await api.post("/events/register", { event_id: ev.id, email: user?.email });
      toast.success(`You're registered for "${ev.title}". Payment step will follow once checkout is connected.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const shown = events.filter((e) => filter === "all" || e.format === filter);

  return (
    <Layout>
      <section className="section-pad">
        <div className="text-center mb-10">
          <EText k="events.pill" className="pill bg-brand-cream2 text-brand-tan">Retreats & Events</EText>
          <EText k="events.title" as="h1" className="font-display text-4xl sm:text-5xl font-semibold text-brand-ink mt-3 block">Gather, Practice & Heal Together</EText>
          <EText k="events.subtitle" as="p" multiline className="mt-3 text-brand-inksoft max-w-2xl mx-auto block">Join us online or in person in San Diego for immersive holistic healing experiences.</EText>
        </div>

        <div className="flex justify-center gap-2 mb-10">
          {[["all", "All"], ["online", "Online"], ["in-person", "In-Person"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${filter === k ? "bg-brand-sage text-white" : "bg-white border border-brand-line text-brand-inksoft"}`}>{l}</button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {shown.map((ev, i) => (
            <motion.div key={ev.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="card-surface overflow-hidden flex flex-col md:flex-row" data-testid={`event-${ev.id}`}>
              <img src={mediaUrl(ev.image)} alt={ev.title} className="md:w-44 h-44 md:h-auto object-cover" />
              <div className="p-5 flex-1 flex flex-col">
                <span className={`pill self-start ${ev.format === "online" ? "bg-brand-cream2 text-brand-sage" : "bg-brand-cream2 text-brand-tan"}`}>
                  {ev.format === "online" ? <><Globe size={11} className="inline mr-1" />Online</> : <><MapPin size={11} className="inline mr-1" />In-Person</>}
                </span>
                <h3 className="font-display text-xl font-semibold text-brand-ink mt-2">{ev.title}</h3>
                <p className="text-xs text-brand-muted mt-1 flex items-center gap-1"><CalendarDays size={12} /> {new Date(ev.date).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</p>
                <p className="text-sm text-brand-inksoft mt-2 flex-1">{ev.description}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-display text-2xl font-semibold text-brand-ink">${ev.price}</span>
                  <button onClick={() => register(ev)} className="btn-primary !py-2 text-sm" data-testid={`register-${ev.id}`}>Register</button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </Layout>
  );
}
